import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const ShopSection = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const searchQuery = searchParams.get("search");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/categories/`)
      .then((res) => {
        setCategories(res.data);
        if (categoryParam && categoryParam !== "all") {
          setSelectedCategoryId(parseInt(categoryParam));
        } else if (searchQuery) {
          const matchingCategory = res.data.find((cat) =>
            cat.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
          if (matchingCategory) {
            setSelectedCategoryId(matchingCategory.id);
          } else {
            setSelectedCategoryId(res.data.length > 0 ? res.data[0].id : null);
          }
        } else {
          setSelectedCategoryId(res.data.length > 0 ? res.data[0].id : null);
        }
      })
      .catch(() => setCategories([]));
  }, [categoryParam, searchQuery]);

  const selectedCategory = categories.find(
    (cat) => cat.id === selectedCategoryId
  );

  const filterMaterials = (materials) => {
    if (!searchQuery) return materials;
    const matchingCategory = categories.find((cat) =>
      cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (matchingCategory && matchingCategory.id === selectedCategory?.id) {
      return materials;
    }
    return materials.filter((material) =>
      material.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filterProductTypes = (productTypes) => {
    if (!searchQuery) return productTypes;
    const matchingCategory = categories.find((cat) =>
      cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (matchingCategory && matchingCategory.id === selectedCategory?.id) {
      return productTypes;
    }
    return productTypes.filter((pt) => {
      const nameMatch = pt.name.toLowerCase().includes(searchQuery.toLowerCase());
      const materialMatch = (pt.materials || []).some((material) =>
        material.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return nameMatch || materialMatch;
    });
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <section className="shop py-80">
      <div className="container container-lg">
        {/* Mobile Toggle */}
        <button
          className="shop-sidebar-toggle d-lg-none"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Toggle Categories"
        >  
          <i className="ph ph-list"></i>
          <span>Categories</span>
        </button>

        {/* Backdrop */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              className="shop-sidebar-backdrop d-lg-none"
              onClick={closeSidebar}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}
        </AnimatePresence>

        <div className="row gy-4">
          {/* Sidebar */}
          <aside className="col-lg-3">
            <motion.div
              className={`shop-sidebar ${sidebarOpen ? "active" : ""}`}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="shop-sidebar-box">
                <h6 className="sidebar-title">Categories</h6>
                <ul className="category-list">
                  {categories.map((category) => {
                    const isSelected = selectedCategoryId === category.id;
                    return (
                      <li
                        key={category.id}
                        className={`category-item ${isSelected ? "active" : ""}`}
                        onClick={() => {
                          setSelectedCategoryId(category.id);
                          setSidebarOpen(false);
                        }}
                      >
                        <span className="category-name">
                          {isSelected && <i className="ph ph-check-circle-fill" />}
                          {category.name}
                        </span>
                        <span className="category-count">
                          {category.product_types ? category.product_types.length : 0}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </motion.div>
          </aside>

          {/* Main Content */}
          <main className="col-lg-9">
            {searchQuery && (
              <motion.div
                className="shop-header search-header"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h5>Search results for: <span className="highlight-text">"{searchQuery}"</span></h5>
                {selectedCategory && <p>Category: <strong>{selectedCategory.name}</strong></p>}
              </motion.div>
            )}

            {categoryParam && !searchQuery && selectedCategory && (
              <motion.div
                className="shop-header category-header"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h5><i className="ph ph-stack" /> {selectedCategory.name}</h5>
              </motion.div>
            )}

            {!selectedCategory || !selectedCategory.product_types || selectedCategory.product_types.length === 0 ? (
              <div className="empty-state">
                <i className="ph ph-package" />
                <p>No product types found for this category.</p>
              </div>
            ) : (
              <div className="product-grid">
                {filterProductTypes(selectedCategory.product_types).length === 0 ? (
                  <div className="empty-state">
                    <i className="ph ph-magnifying-glass" />
                    <p>No products found matching "{searchQuery}"</p>
                  </div>
                ) : (
                  <motion.div
                    key={`${selectedCategoryId}-${searchQuery}`}
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: { opacity: 0 },
                      show: {
                        opacity: 1,
                        transition: { staggerChildren: 0.1 }
                      }
                    }}
                  >
                    {filterProductTypes(selectedCategory.product_types).map((productType) => {
                      const filteredMaterials = filterMaterials(productType.materials || []);
                      if (searchQuery && filteredMaterials.length === 0) return null;

                      return (
                        <motion.div
                          key={productType.id}
                          className="product-type-card"
                          variants={{
                            hidden: { opacity: 0, y: 30 },
                            show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
                          }}
                        >
                          <div className="product-type-header">
                            <h5 className="product-type-title">
                              <i className="ph ph-package" /> {productType.name}
                            </h5>
                            {productType.is_custom_size && (
                              <span className="custom-size-badge">Custom Size Available</span>
                            )}
                          </div>

                          {filteredMaterials.length > 0 ? (
                            <div className="row gy-4">
                              {filteredMaterials.map((material) => (
                                <div key={material.id} className="col-6 col-md-4">
                                  <div className="material-card">
                                    <div className="material-img-box">
                                      <img
                                        src={material.image_swatch || material.swatch_url || "assets/images/thumbs/default-material.png"}
                                        alt={material.name}
                                        onError={(e) => { e.target.src = "assets/images/thumbs/default-material.png"; }}
                                      />
                                    </div>
                                    <div className="material-info">
                                      <h6 className="material-name">{material.name}</h6>
                                      {material.material_cost_per_sqft && (
                                        <div className="material-price">${material.material_cost_per_sqft} / sqft</div>
                                      )}
                                    </div>
                                    <Link
                                      to={`/product-details-material?type=${productType.id}&material=${material.id}`}
                                      className="btn btn-main customize-btn"
                                    >
                                      Customize now <i className="ph ph-shopping-cart" />
                                    </Link>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-materials text-muted">No materials available.</div>
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
